// TechnicianSelect.js - OPRAVENÁ VERZE pro řešení problému s technikami

import React, { useState, useRef, useEffect } from 'react';
import { Search, X, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const TechnicianSelect = ({ technicians = [], selectedTechnicians = [], onChange }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  // Ensure we always have arrays to work with
  const safeTechnicians = Array.isArray(technicians) ? technicians : [];
  const safeSelectedTechnicians = Array.isArray(selectedTechnicians) ? selectedTechnicians : [];

  // Handle clicking outside of dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredTechnicians = safeTechnicians.filter(tech => {
    if (!tech || !tech.name) {
      return false;
    }
    return tech.name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const toggleTechnician = (techId) => {
    if (!techId || !onChange) {
      return;
    }
    
    const newSelected = safeSelectedTechnicians.includes(techId)
      ? safeSelectedTechnicians.filter(id => id !== techId)
      : [...safeSelectedTechnicians, techId];
    
    onChange(newSelected);
  };

  const getSelectedTechnicianNames = () => {
    const selectedNames = safeTechnicians
      .filter(tech => {
        if (!tech) return false;
        
        // Handle both id (D1) and _id (MongoDB) for backward compatibility
        const techId = tech.id || tech._id;
        const isSelected = safeSelectedTechnicians.includes(techId);
        
        return isSelected;
      })
      .map(tech => tech.name || 'Unknown');
    
    return selectedNames.join(', ');
  };

  // Helper function to get tech ID (handles both formats)
  const getTechId = (tech) => {
    if (!tech) return null;
    const id = tech.id || tech._id;
    return id;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {t('projects.assignedTechnicians')}
      </label>
      
      {/* Hlavní výběrové pole */}
      <div
        className="border rounded-md p-2 cursor-pointer bg-white hover:bg-gray-50 min-h-[40px] flex items-center border-gray-300"
        onClick={() => setIsOpen(!isOpen)}
      >
        {safeSelectedTechnicians.length > 0 ? (
          <div className="text-sm">{getSelectedTechnicianNames()}</div>
        ) : (
          <div className="text-sm text-gray-500">
            {t('projects.selectTechnicians', 'Vyberte techniky...')}
          </div>
        )}
      </div>

      {/* Dropdown seznam */}
      {isOpen && (
        <div className="absolute z-10 mt-1 w-full bg-white border rounded-md shadow-lg">
          {/* Search input */}
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
              <input
                type="text"
                className="pl-8 pr-4 py-2 w-full border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={t('projects.searchTechnicians', 'Hledat techniky...')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>

          {/* Technicians list */}
          <div className="max-h-60 overflow-y-auto">
            {filteredTechnicians.length > 0 ? (
              filteredTechnicians.map((tech) => {
                const techId = getTechId(tech);
                
                if (!techId) {
                  return null;
                }
                
                return (
                  <div
                    key={techId}
                    className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleTechnician(techId);
                    }}
                  >
                    <div className="flex items-center flex-1">
                      <div className={`w-4 h-4 border rounded mr-3 flex items-center justify-center ${
                        safeSelectedTechnicians.includes(techId) ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
                      }`}>
                        {safeSelectedTechnicians.includes(techId) && (
                          <Check className="h-3 w-3 text-blue-600" />
                        )}
                      </div>
                      <span className="text-sm">{tech.name || 'Unknown'}</span>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="px-4 py-2 text-sm text-gray-500">
                {searchTerm 
                  ? t('projects.noTechniciansFound', 'Žádní technici nenalezeni')
                  : 'Žádní technici k dispozici'
                }
              </div>
            )}
          </div>

          {/* Selected count */}
          <div className="p-2 border-t text-xs text-gray-500 bg-gray-50">
            {t('projects.techniciansSelected', '{{count}} technik vybrán', { 
              count: safeSelectedTechnicians.length 
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default TechnicianSelect;